/**
 * Session Replay shortcut wrapper.
 */
import {
  SessionReplay,
  type SessionReplayFlushMetadata,
  type SessionReplayFlushReason,
  type SessionReplayOptions,
  type SessionReplayStatus,
} from "../../features/analytics/replay";
import type { Teardown } from "../../utils/browser";

export class ReplayTracker {
  constructor(public readonly client: SessionReplay) {}

  /**
   * Start recording the current page. Returns a teardown that stops capture
   * and flushes the final buffer.
   *
   * @example
   * ```ts
   * const stop = await fg.replay.start({ sampleRate: 1.0, maskAllInputs: true });
   * ```
   */
  start(options?: SessionReplayOptions): Promise<Teardown> {
    return this.client.start(options ?? {});
  }

  /** Enable replay and start recording immediately. */
  enable(options?: SessionReplayOptions): Promise<Teardown> {
    return this.client.enable(options ?? {});
  }

  /** Disable replay and stop any active recorder. */
  disable(): void {
    this.client.disable();
  }

  /** Stop the active recorder and flush a final chunk. */
  stop(reason?: SessionReplayFlushReason): void {
    this.client.stop(reason);
  }

  /** Flush buffered events without stopping recording. */
  flush(reason?: SessionReplayFlushReason): Promise<void> {
    return this.client.flush(reason ?? "manual");
  }

  /** Bind a known user ID to subsequent replay chunks. */
  identify(userId: string): void {
    this.client.identify(userId);
  }

  /** Read recorder lifecycle and health state. */
  status(): SessionReplayStatus {
    return this.client.getStatus();
  }

  /** Send a pre-recorded rrweb chunk for custom recorder integrations. */
  sendChunk(
    sessionId: string,
    events: unknown[],
    options?: Parameters<SessionReplay["sendChunk"]>[2],
  ): Promise<void> {
    return this.client.sendChunk(sessionId, events, options ?? {});
  }
}

export type { SessionReplayFlushMetadata, SessionReplayFlushReason, SessionReplayOptions, SessionReplayStatus };
